import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getUser } from 'store/selectors/auth.selector';
import { getDealSourceDataFlags } from 'store/selectors/RACAffordabilityEngine.selector';
import { getFetchingLendersFlag, getLenderState, getLendersList } from 'store/selectors/lenders.selector';
import { getDisabledFeaturesList } from 'store/selectors/ui.selector';
import { SSO_TYPE } from 'modules/core/constants';
import { DEALER_SSO_LENDER_SELECTED_EVENT } from 'modules/auth/constants/auth.const';
import { getMakeItWorkForAllLendersLinks, getSelectableLenderLinks } from 'modules/core/components/SideBar/utils';
import { SELECT_LENDER_MODAL_OPEN_BODY_CLASS } from 'components/Modals/SelectLenderDialog/styles';
import {
  getDealerSsoModalLenderLinks,
  getDealerSsoSelectedLenderPath,
  getDealerSsoSidebarLenderPaths,
  isDealerSsoLenderSelectionComplete,
  isDealerSsoNonModalNavigationPath,
  saveDealerSsoSelectedLenderPath,
} from 'modules/auth/utils/dealerSsoLenderSelection.utils';

const SELECT_LENDER_BLUR_REMOVE_DELAY_MS = 300;

export const useDealerSsoLenderSelection = () => {
  const history = useHistory();
  const location = useLocation();
  const user = useSelector(getUser);
  const lenders = useSelector(getLendersList);
  const disabledFeaturesList = useSelector(getDisabledFeaturesList);
  const isFetchingLendersList = useSelector(getFetchingLendersFlag);
  const lenderState = useSelector(getLenderState);
  const racDealSourceDataFlags = useSelector(getDealSourceDataFlags);

  const isDealSourceReceived = lenderState?.dealSourceDataFlags?.isReceived || racDealSourceDataFlags.isReceived;

  const [savedLenderPath, setSavedLenderPath] = useState<string | null>(() => getDealerSsoSelectedLenderPath());

  const isDealerSso = user?.is_sso === true && user.sso_type === SSO_TYPE.DEALER;

  const redirectFreeLenders = useMemo(() => lenders.filter((l) => l.withoutLenderRedirect), [lenders]);

  const selectableLenders = useMemo(
    () => getSelectableLenderLinks(redirectFreeLenders, disabledFeaturesList, user?.role),
    [redirectFreeLenders, disabledFeaturesList, user?.role],
  );

  const secondaryLinks = useMemo(
    () => getMakeItWorkForAllLendersLinks(redirectFreeLenders, disabledFeaturesList, user?.role),
    [redirectFreeLenders, disabledFeaturesList, user?.role],
  );

  const allModalLenders = useMemo(
    () => getDealerSsoModalLenderLinks(redirectFreeLenders, disabledFeaturesList, user?.role),
    [redirectFreeLenders, disabledFeaturesList, user?.role],
  );

  const allModalLenderPaths = useMemo(() => allModalLenders.map((link) => link.link as string), [allModalLenders]);

  const allSidebarLenderPaths = useMemo(() => getDealerSsoSidebarLenderPaths(lenders), [lenders]);

  const isSelectionComplete = isDealerSsoLenderSelectionComplete(user, redirectFreeLenders, disabledFeaturesList);

  const showModal = isDealerSso && !isFetchingLendersList && allModalLenders.length > 1 && !savedLenderPath;

  useLayoutEffect(() => {
    if (showModal) {
      document.body.classList.add(SELECT_LENDER_MODAL_OPEN_BODY_CLASS);
    }
  }, [showModal]);

  useEffect(() => {
    if (showModal) return undefined;

    const timeoutId = window.setTimeout(() => {
      document.body.classList.remove(SELECT_LENDER_MODAL_OPEN_BODY_CLASS);
    }, SELECT_LENDER_BLUR_REMOVE_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [showModal]);

  useEffect(
    () => () => {
      document.body.classList.remove(SELECT_LENDER_MODAL_OPEN_BODY_CLASS);
    },
    [],
  );

  useEffect(() => {
    window.dispatchEvent(new Event(DEALER_SSO_LENDER_SELECTED_EVENT));
  }, [isSelectionComplete, savedLenderPath]);

  useLayoutEffect(() => {
    if (!isDealerSso || isFetchingLendersList || !isDealSourceReceived || savedLenderPath) return;

    if (allModalLenders.length !== 1) return;

    const lenderPath = allModalLenders[0].link as string;

    saveDealerSsoSelectedLenderPath(lenderPath);
    setSavedLenderPath(lenderPath);
    window.dispatchEvent(new Event(DEALER_SSO_LENDER_SELECTED_EVENT));

    if (location.pathname !== lenderPath) {
      history.replace(lenderPath);
    }
  }, [
    isDealerSso,
    isFetchingLendersList,
    isDealSourceReceived,
    savedLenderPath,
    allModalLenders,
    history,
    location.pathname,
  ]);

  useLayoutEffect(() => {
    if (!isDealerSso || !savedLenderPath) return;

    const { pathname } = location;

    if (pathname === savedLenderPath) return;

    if (allModalLenderPaths.includes(pathname)) {
      saveDealerSsoSelectedLenderPath(pathname);
      setSavedLenderPath(pathname);

      return;
    }

    if (user?.isWithInventory && allSidebarLenderPaths.includes(pathname)) {
      saveDealerSsoSelectedLenderPath(pathname);
      setSavedLenderPath(pathname);

      return;
    }

    if (isDealerSsoNonModalNavigationPath(pathname)) {
      return;
    }

    history.replace(savedLenderPath);
  }, [
    isDealerSso,
    savedLenderPath,
    history,
    location,
    allModalLenderPaths,
    user?.isWithInventory,
    allSidebarLenderPaths,
  ]);

  const handleSelect = useCallback(
    (lenderPath: string) => {
      saveDealerSsoSelectedLenderPath(lenderPath);
      setSavedLenderPath(lenderPath);
      window.dispatchEvent(new Event(DEALER_SSO_LENDER_SELECTED_EVENT));

      if (lenderPath !== window.location.pathname) {
        history.push(lenderPath);
      }
    },
    [history],
  );

  return {
    showModal,
    selectableLenders,
    secondaryLinks,
    handleSelect,
  };
};
